// Client for the Platform Insights admin views. Backend: api/core/routers/admin_insights.py (platform admins only).

import { ApiError, TokenGetter } from './learningApi';

export type InsightWindow = { days: number; since: string; until: string };

export interface Retirement {
  replacement: string | null;
  image_replacement: string | null;
  sunset_date: string | null;
}

export interface RetiredModelRow extends Retirement {
  kind: 'function' | 'chain';
  customer_id: string;
  customer_tag: string | null;
  name: string;
  step: string | null;
  function_id: string | null;
  chain_id: string | null;
  node_id: string | null;
  retired_model: string;
  runs: number;
  ok: number;
  answered_by: string | null;
  last_run: string | null;
  in_config: boolean;
  config_detail: string | null;
}

export interface RetiredModels {
  window: InsightWindow;
  retired: (Retirement & { model: string })[];
  models: (Retirement & { model: string; runs: number; customers: number; targets: number; in_config: number })[];
  rows: RetiredModelRow[];
  truncated: boolean;
}

export type FailureKind = 'rate_limit' | 'not_servable' | 'timeout' | 'server' | 'auth' | 'other';
export type ZdrRoute = 'none' | 'fastest' | 'us' | 'caller';

export interface CallCounts {
  calls: number;
  failures: number;
  failure_rate: number | null;
  kinds: Record<FailureKind, number>;
}

export interface ProviderRow extends CallCounts {
  provider: string;
  models: number;
  regions: (CallCounts & { region: string; route: ZdrRoute; route_label: string })[];
}

export interface ProviderIncident {
  model: string;
  provider: string;
  start: string;
  end: string;
  minutes: number;
  state: 'degraded' | 'down';
  reasons: string[];
  ongoing: boolean;
  provider_wide: boolean;
}

export interface ProviderWidePeriod {
  provider: string;
  start: string;
  end: string;
  minutes: number;
  state: 'degraded' | 'down';
  models: string[];
  reason: string;
  ongoing: boolean;
}

export interface ProviderHealth {
  window: InsightWindow & { unit: 'hour' | 'day' };
  kinds: { key: FailureKind; label: string }[];
  providers: ProviderRow[];
  models: (CallCounts & { model: string; provider: string })[];
  series: (CallCounts & { bucket: string; provider: string })[];
  incidents: ProviderIncident[];
  provider_wide: ProviderWidePeriod[];
  health_history_available: boolean;
  truncated: boolean;
}

export interface CustomerHealthRow {
  customer_id: string;
  customer_tag: string | null;
  runs: number;
  prev_runs: number;
  runs_change: number | null;
  error_rate: number | null;
  prev_error_rate: number | null;
  error_rate_delta: number | null;
  backup_share: number | null;
  backup_share_delta: number | null;
  p95_ms: number | null;
  prev_p95_ms: number | null;
  p95_delta_ms: number | null;
  credits: number;
  prev_credits: number;
  credits_delta: number;
  warning_functions: number;
  warning_chains: number;
  regressions: { code: string; label: string }[];
  score: number;
}

export interface CustomerHealth {
  window: InsightWindow & { previous_since: string };
  warning_window_days: number;
  min_runs: number;
  customers: CustomerHealthRow[];
  truncated: boolean;
}

export interface CutoffCounts {
  runs: number;
  limited: number;
  cut_offs: number;
  rescued_backup: number;
  recovered_same_model: number;
  failed_after_cutoff: number;
  cut_off_share: number | null;
  rescue_share: number | null;
  failed_after_share: number | null;
}

export type LimitMode = 'off' | 'fixed' | 'auto' | 'chain' | 'none';

interface LimitInfo {
  customer_id: string;
  customer_tag: string | null;
  mode: LimitMode;
  preset: string | null;
  limit_now_s: number | null;
  limit_note: string;
  applied_limit_s: number | null;
  p95_ms: number | null;
  headroom_s: number | null;
}

export type FunctionLimitRow = LimitInfo & CutoffCounts & { function_name: string; function_id: string | null; family_id: string | null };

export type ChainLimitRow = LimitInfo & {
  chain_id: string;
  chain_name: string;
  runs: number;
  cut_offs: number;
  cut_off_share: number | null;
  completed_with_backup_step: number;
};

export interface TimeLimits {
  window: InsightWindow;
  overall: CutoffCounts;
  by_mode: (CutoffCounts & { mode: LimitMode; preset: string | null; functions: number })[];
  functions: FunctionLimitRow[];
  chains: ChainLimitRow[];
  chain_overall: { runs: number; cut_offs: number; cut_off_share: number | null };
  truncated: boolean;
}

export function adminInsightsApi(getToken: TokenGetter) {
  async function get<T>(path: string): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/admin/insights${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    retiredModels: (days: number) => get<RetiredModels>(`/retired-models?days=${days}`),
    providers: (days: number) => get<ProviderHealth>(`/providers?days=${days}`),
    customers: (days: number) => get<CustomerHealth>(`/customers?days=${days}`),
    timeLimits: (days: number) => get<TimeLimits>(`/time-limits?days=${days}`),
  };
}
